
import { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import OrdersTable from "@/components/admin/orders/OrdersTable";
import OrderFilters from "@/components/admin/orders/OrderFilters";
import OrderDetailsModal from "@/components/admin/orders/OrderDetailsModal";
import OrderStats from "@/components/admin/orders/OrderStats";
import { useOrders } from "@/hooks/useOrders";
import { useOrderManagement } from "@/hooks/useOrderManagement";
import { getSafeProfile, type SafeOrderProfile } from "@/utils/typeGuards";

interface OrderProfile {
  full_name: string | null;
  email: string | null;
}

interface OrderItem {
  id: string;
  quantity: number;
  price: number;
  products: {
    name: string;
    image_url?: string;
  } | null;
}

interface Order {
  id: string;
  user_id: string | null;
  total_amount: number;
  status: string | null;
  payment_status: string | null;
  payment_method: string | null;
  created_at: string | null;
  updated_at: string | null;
  tracking_number?: string | null;
  profiles: SafeOrderProfile;
  order_items?: OrderItem[];
}

const AdminOrders = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const { orders, isLoading } = useOrders({
    searchTerm,
    statusFilter,
    paymentStatusFilter,
    dateFilter,
  });

  const { updateOrderStatus } = useOrderManagement();

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
  };

  const handleUpdateStatus = async (orderId: string, status: string) => {
    await updateOrderStatus(orderId, status);
  };

  // Transform orders to ensure safe profile access
  const typedOrders: Order[] = (orders || []).map(order => ({
    ...order,
    profiles: getSafeProfile(order.profiles)
  }));

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-admin-foreground">Order Management</h1>
          <p className="text-admin-muted-foreground mt-1">
            Manage customer orders, track status, and handle fulfillment
          </p>
        </div>

        <OrderStats />

        <OrderFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          paymentStatusFilter={paymentStatusFilter}
          onPaymentStatusChange={setPaymentStatusFilter}
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
        />

        <OrdersTable
          orders={typedOrders}
          isLoading={isLoading}
          onViewOrder={handleViewOrder}
          onUpdateStatus={handleUpdateStatus}
        />

        <OrderDetailsModal
          order={selectedOrder}
          open={!!selectedOrder}
          onOpenChange={(open) => !open && setSelectedOrder(null)}
          onUpdateStatus={handleUpdateStatus}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
